import { FONT_MONO } from '../../config/fonts';
import dayjs from 'dayjs'
import { Box, Typography, alpha } from '@mui/material'
import { History, Pause, PlayArrow, SensorsOutlined } from '@mui/icons-material'

import { glassSx } from './overlayStyles'

// Clock shown over the map.
//  live   (Radar):  teal + pulsing green "LIVE" dot, follows the wall clock
//  replay (Review): amber + "REPLAY" with play state and speed, follows the replay timeline
// The two never look alike, so a screenshot or a glance tells which time you are looking at.
export const CLOCK_THEMES = {
  live: { accent: '#19E6C1', badge: '#2BE36B', label: 'LIVE', Icon: SensorsOutlined },
  replay: { accent: '#FFB020', badge: '#FFB020', label: 'REPLAY', Icon: History },
}

const utcOffset = (t) => {
  const min = t.utcOffset()
  const sign = min >= 0 ? '+' : '-'
  const h = Math.floor(Math.abs(min) / 60)
  const m = Math.abs(min) % 60
  return `UTC${sign}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`
}

const MapClock = ({ time, mode = 'live', isPlaying = false, speed = 1, top = 16, left = 56 }) => {
  const t = dayjs(time)
  const theme = CLOCK_THEMES[mode] || CLOCK_THEMES.live
  const { accent, badge, label, Icon } = theme
  const isLive = mode === 'live'

  return (
    <Box
      role="timer"
      aria-label={`${label} time ${t.format('YYYY-MM-DD HH:mm:ss')}`}
      sx={{
        position: 'absolute',
        top,
        left,
        zIndex: 1300,
        pointerEvents: 'none',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'stretch',
        overflow: 'hidden',
        borderRadius: '10px',
        color: '#fff',
        ...glassSx(accent),
      }}
    >
      {/* Mode stripe: solid for live, hatched for replay */}
      <Box
        sx={{
          width: 5,
          flexShrink: 0,
          background: isLive
            ? accent
            : `repeating-linear-gradient(45deg, ${accent} 0 4px, ${alpha(accent, 0.35)} 4px 8px)`,
        }}
      />

      <Box px={1.75} py={0.9} minWidth={210}>
        {/* Badge row */}
        <Box display="flex" alignItems="center" gap={0.75} mb={0.25}>
          {isLive ? (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: badge,
                boxShadow: `0 0 0 0 ${alpha(badge, 0.7)}`,
                animation: 'mapClockPulse 1.6s ease-out infinite',
                '@keyframes mapClockPulse': {
                  '0%': { boxShadow: `0 0 0 0 ${alpha(badge, 0.7)}` },
                  '70%': { boxShadow: `0 0 0 7px ${alpha(badge, 0)}` },
                  '100%': { boxShadow: `0 0 0 0 ${alpha(badge, 0)}` },
                },
              }}
            />
          ) : (
            <Icon sx={{ fontSize: '0.95rem', color: badge }} />
          )}

          <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.16em', color: badge }}>
            {label}
          </Typography>

          {!isLive && (
            <Box
              display="flex"
              alignItems="center"
              gap={0.25}
              sx={{
                ml: 0.5,
                px: 0.75,
                borderRadius: '4px',
                fontSize: '0.64rem',
                fontWeight: 800,
                letterSpacing: '0.08em',
                color: isPlaying ? '#1a1205' : alpha('#fff', 0.75),
                backgroundColor: isPlaying ? accent : alpha('#fff', 0.1),
              }}
            >
              {isPlaying ? <PlayArrow sx={{ fontSize: '0.8rem' }} /> : <Pause sx={{ fontSize: '0.8rem' }} />}
              {isPlaying ? `${speed}x` : 'PAUSED'}
            </Box>
          )}

          <Box flex={1} />
          {isLive && <Icon sx={{ fontSize: '0.95rem', color: alpha(accent, 0.8) }} />}
        </Box>

        {/* Time: seconds a little smaller so the eye lands on hours:minutes */}
        <Box display="flex" alignItems="baseline" sx={{ fontFamily: FONT_MONO }}>
          <Typography
            component="span"
            sx={{
              fontFamily: 'inherit',
              fontSize: '1.75rem',
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: '0.04em',
              color: accent,
              textShadow: `0 1px 2px rgba(0,0,0,0.9), 0 0 10px ${alpha(accent, 0.35)}`,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {t.format('HH:mm')}
          </Typography>
          <Typography
            component="span"
            sx={{
              fontFamily: 'inherit',
              fontSize: '1.15rem',
              fontWeight: 600,
              ml: 0.25,
              color: alpha(accent, 0.75),
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            :{t.format('ss')}
          </Typography>
        </Box>

        <Typography sx={{ fontSize: '0.72rem', color: alpha('#fff', 0.62), letterSpacing: '0.02em' }}>
          {t.format('ddd, D MMM YYYY')} · {utcOffset(t)}
        </Typography>
      </Box>
    </Box>
  )
}

export default MapClock
